import { Theme } from '@mui/system';
import { GridSortModel } from '@mui/x-data-grid-pro';
import moment from 'moment-timezone';
import { OnlyFansMetricUserSalesDetailsRiskLevel } from '../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { d2f } from '../../../../utils/common';

/**
 * The subset of a user row the card list needs to search and sort. `timelineValue` is computed by the
 * parent (percentage of the metric's lifetime the user was spending) so it can be sorted like a column.
 */
export type SortableUser = {
    _id: string;
    userId: number;
    userName: string;
    riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel;
    subscribedAt?: Date | string | null;
    total: number | object;
    totalChargebacks: number | object;
    timelineValue?: number;
};

export type UserSortField = 'total' | 'subscribedAt' | 'riskLevel' | 'timeline' | 'totalChargebacks' | 'userId' | 'userName';

export const USER_SORT_OPTIONS: { field: UserSortField; label: string }[] = [
    { field: 'total', label: 'Total spent' },
    { field: 'subscribedAt', label: 'Subscribed date' },
    { field: 'riskLevel', label: 'Risk level' },
    { field: 'timeline', label: 'Spending timeline' },
    { field: 'totalChargebacks', label: 'Chargebacks' },
    { field: 'userId', label: 'User ID' },
    { field: 'userName', label: 'Username' },
];

/**
 * Money fields arrive either as plain numbers or as Mongo Decimal128 objects.
 */
export const moneyToNumber = (value: number | object | null | undefined): number => (typeof value === 'number' ? value : d2f(value));

type MoneyRow = {
    total: number | object;
    totalWithoutSubscriptions: number | object;
    totalChargebacks: number | object;
    totalChargebacksWithoutSubscriptions: number | object;
};

/**
 * Total spent, honouring the page-level gross/net and with/without subscriptions toggles.
 */
export const userTotalSpent = (row: MoneyRow, showEarningsAsGross: boolean, showEarningsWithSubscriptions: boolean): number => {
    const total = moneyToNumber(showEarningsWithSubscriptions ? row.total : row.totalWithoutSubscriptions);

    return showEarningsAsGross ? total : total * 0.8;
};

/**
 * Total charged back, honouring the same toggles as `userTotalSpent`.
 */
export const userTotalChargebacks = (row: MoneyRow, showEarningsAsGross: boolean, showEarningsWithSubscriptions: boolean): number => {
    const total = moneyToNumber(showEarningsWithSubscriptions ? row.totalChargebacks : row.totalChargebacksWithoutSubscriptions);

    return showEarningsAsGross ? total : total * 0.8;
};

export const getRiskLevelColor = (riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel, theme: Theme): string => {
    switch (riskLevel) {
        case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
            return theme.palette.error.dark;
        case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
            return theme.palette.error.main;
        case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
            return theme.palette.warning.main;
        case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
            return theme.palette.warning.light;
        case OnlyFansMetricUserSalesDetailsRiskLevel.LOW:
            return theme.palette.info.main;
        default:
            return 'inherit';
    }
};

export const getRiskLevelLabel = (riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel): string => {
    switch (riskLevel) {
        case OnlyFansMetricUserSalesDetailsRiskLevel.EXTREME:
            return 'Extreme Risk';
        case OnlyFansMetricUserSalesDetailsRiskLevel.VERY_HIGH:
            return 'Very High Risk';
        case OnlyFansMetricUserSalesDetailsRiskLevel.HIGH:
            return 'High Risk';
        case OnlyFansMetricUserSalesDetailsRiskLevel.MODERATE:
            return 'Moderate Risk';
        case OnlyFansMetricUserSalesDetailsRiskLevel.LOW:
            return 'Low Risk';
        default:
            return 'No Risk';
    }
};

export const subscribedAtString = (subscribedAt: null | undefined | Date | string, timezone: string): string => {
    if (typeof subscribedAt === 'undefined' || subscribedAt === null) {
        return 'Not Found';
    }

    return moment(subscribedAt).tz(timezone).format('L hh:mma');
};

/**
 * Case-insensitive match on username or user ID. Empty search returns the input untouched.
 */
export const searchUsers = <T extends Pick<SortableUser, 'userId' | 'userName'>>(rows: T[], search: string): T[] => {
    const term = search.trim().toLowerCase();

    if (term.length === 0) {
        return rows;
    }

    return rows.filter(row => String(row.userId).includes(term) || (row.userName || '').toLowerCase().includes(term));
};

const dateMillis = (value: Date | string | null | undefined): number | null => {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    const parsed = moment(value);

    return parsed.isValid() ? parsed.valueOf() : null;
};

/**
 * Compare two rows on one field. Positive means `a` sorts after `b` in ascending order.
 */
const compareByField = (a: SortableUser, b: SortableUser, field: string): number | null => {
    switch (field) {
        case 'total':
            return moneyToNumber(a.total) - moneyToNumber(b.total);
        case 'totalChargebacks':
            return moneyToNumber(a.totalChargebacks) - moneyToNumber(b.totalChargebacks);
        case 'riskLevel':
            return a.riskLevel - b.riskLevel;
        case 'timeline':
            return (a.timelineValue || 0) - (b.timelineValue || 0);
        case 'userId':
            return a.userId - b.userId;
        case 'userName':
            return (a.userName || '').localeCompare(b.userName || '', undefined, { sensitivity: 'base' });
        case 'subscribedAt':
            return (dateMillis(a.subscribedAt) || 0) - (dateMillis(b.subscribedAt) || 0);
        default:
            return null;
    }
};

/**
 * Sort rows by the first entry of a DataGrid sort model, mirroring the grid's column comparators so the
 * card list and the table agree. Rows without a subscription date always sort last. Returns a new array.
 */
export const sortUsers = <T extends SortableUser>(rows: T[], sortModel: GridSortModel): T[] => {
    const primary = sortModel[0];

    if (!primary || !primary.sort) {
        return rows;
    }

    const { field, sort } = primary;
    const direction = sort === 'desc' ? -1 : 1;

    return [...rows].sort((a, b) => {
        if (field === 'subscribedAt') {
            const aMissing = dateMillis(a.subscribedAt) === null;
            const bMissing = dateMillis(b.subscribedAt) === null;

            if (aMissing && bMissing) return 0;
            if (aMissing) return 1;
            if (bMissing) return -1;
        }

        const result = compareByField(a, b, field);

        return result === null ? 0 : result * direction;
    });
};
